import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { FaEdit, FaInfoCircle } from "react-icons/fa"; // Import the edit icon
import axios from "axios";
import Grid from "../components/Grid"; // Import the Grid component
import { toast } from "react-toastify";
import { Checkbox, FormControlLabel, Tooltip } from "@mui/material";
import { saveAs } from "file-saver";
import { FaDownload } from "react-icons/fa"; 
import ExcelJS from "exceljs";

// Use the environment variable for the API base URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function Products({ onAddProductClick }) {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState(""); // New state for selected unit
  const [selectedPartialQty, setSelectedPartialQty] = useState("");
  const [selectedAllowQuantityModification, setSelectedAllowQuantityModification] = useState("");
  const [selectedAllowSalesOrderInclusive, setSelectedAllowSalesOrderInclusive] = useState("");
  const [units, setUnits] = useState([]); // State to hold units fetched from API
  const [sort] = useState({ field: "product_name", direction: "asc" });
  const [selectedSearchField, setSelectedSearchField] =
    useState("product_name"); // New state for search field
  const [userRole, setUserRole] = useState("");
  const [selectedImage, setSelectedImage] = useState([]);
  const [imagePreview, setImagePreview] = useState(null); // New state for preview
  const [allowPartialQuantity, setAllowPartialQuantity] = useState();
  const [allowQuantityModification, setAllowQuantityModification] = useState();
  const [allowSalesOrderInclusive, setAllowSalesOrderInclusive] = useState();  

  useEffect(() => {
    // Fetch the logged-in user's role
    const fetchUserRole = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          toast.error("Authentication token is missing. Please log in again.");
          return;
        }

        const response = await axios.get(`${API_BASE_URL}/users/specific`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setUserRole(response.data.role);
      } catch (error) {
        console.error("Error fetching user role:", error);
        toast.error("Error fetching user role");
      }
    };

    // Fetch products from the API when the component mounts
    const fetchProducts = async () => {
      try {
        const token = localStorage.getItem("token"); // Retrieve token from localStorage
        if (!token) {
          toast.error("Authentication token is missing. Please log in again.");
          return;
        }

        const response = await axios.get(`${API_BASE_URL}/products`, {
          headers: {
            Authorization: `Bearer ${token}`, // Include token in Authorization header
          },
        });

        // Map the products to include an id field
        const productsWithId = response.data.products.map((product) => ({
          ...product,
          id: product.product_code, // Assign product_code to id
        }));
        setProducts(productsWithId); // Set products with id field
      } catch (error) {
        console.error(
          "Error fetching products:",
          error.response?.data || error.message
        );
        toast.error(error.response?.data?.message || "Error fetching products");
      }
    };

    // Fetch categories from the API when the component mounts
    const fetchCategories = async () => {
      try {
        const token = localStorage.getItem("token"); // Assuming token is saved in localStorage
    
        if (!token) {
          toast.error("No token found. Please log in.");
          return;
        }
    
        const response = await axios.get(`${API_BASE_URL}/categories`, {
          headers: {
            Authorization: `Bearer ${token}`, // Include token in the Authorization header
          },
        });
    
        // Assuming the response contains categories related to the user's organization
        setCategories(response.data.categories); // Set the filtered categories based on the user's organization
      } catch (error) {
        console.error("Error fetching categories:", error);
        toast.error("Error fetching categories");
      }
    };

    // Fetch units from the API when the component mounts
    const fetchUnits = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/units`);
        setUnits(response.data.units); // Assuming the response has a `units` field
      } catch (error) {
        console.error("Error fetching units:", error);
        toast.error("Error fetching units");
      }
    };

    fetchProducts();
    fetchCategories();
    fetchUnits();
    fetchUserRole();
  }, []);

  const handleProductClick = (product) => {
    // Ensure that the selected product contains all necessary fields
    setSelectedProduct({
      ...product,
      product_category: product.product_category?._id || "", // Handle nested category
      unit: product.unit?._id || "", // Handle nested unit
      product_description: product.product_description || "",
      product_countInStock: product.product_countInStock || 0,
      status: product.status || "Active",
      product_image: product.product_image || "", // Ensure the image URL is retained
      partial_qty_allocation: product.partial_qty_allocation,
      qty_modifiable: product.qty_modifiable,
      so_inclusive: product.so_inclusive,
      
    });
    // Set the selectedCategory and selectedUnit states
    setSelectedCategory(product.product_category?.category_name || ""); // Set category_name
    setSelectedUnit(product.unit?.unit_name || ""); // Set unit_name
    setSelectedPartialQty(product.partial_qty_allocation);
    setAllowPartialQuantity(product.partial_qty_allocation);
    setSelectedAllowQuantityModification(product.qty_modifiable);
    setAllowQuantityModification(product.qty_modifiable);
    setSelectedAllowSalesOrderInclusive(product.so_inclusive);
    setAllowSalesOrderInclusive(product.so_inclusive);

    // Ensure the correct image preview is shown
    if (product.product_image) {
      setImagePreview(`${process.env.REACT_APP_IMAGE_BASE_URL}${product.product_image}`);
    } else {
      setImagePreview(null);
    }

    setSelectedImage(null); // Reset new image selection
  };

  const handleClosePopup = () => {
    setSelectedProduct(null);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      setImagePreview(URL.createObjectURL(file)); // Show the new image preview
    }
  };

  const handlePartialQuantityChange = (event) => {
    const newValue = event.target.checked;
    setAllowPartialQuantity(newValue);
    setSelectedPartialQty(newValue); // Update selectedPartialQty to ensure it's sent correctly
  };

  const handleQuantityModificationChange = (event) => {
    const newValue = event.target.checked;
    setAllowQuantityModification(newValue);
    setSelectedAllowQuantityModification(newValue); // Update selectedAllowQuantityModification to ensure it's sent correctly
  };

  const handleSalesOrderInclusiveChange = (event) => {
    const newValue = event.target.checked;
    setAllowSalesOrderInclusive(newValue);
    setSelectedAllowSalesOrderInclusive(newValue); // Update selectedAllowSalesOrderInclusive to ensure it's sent correctly
  };

  const handleDownloadExcel = async () => {
  const dataToExport =
    searchTerm.trim().length > 0 ? filteredProducts : products;

    if (dataToExport.length === 0) {
      toast.warn("No product data to export.");
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Product List");

    const headerRow = [
      "Product Code",
      "Product Name",
      "Product Description",
      "Category",
      "Unit",
      "Status",
    ];

    worksheet.addRow(headerRow);

    const header = worksheet.getRow(1);
    header.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD966" },
      };
      cell.font = { bold: true };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    dataToExport.forEach((product) => {
      const row = [
        product.product_code || "N/A",
        product.product_name || "N/A",
        product.product_description || "N/A",
        product.product_category?.category_name || "N/A",
        product.unit?.unit_name || "N/A",
        product.status || "N/A",
      ];

      if (userRole === "Main Admin") {
        row.push(product.organization?.organization_name || "N/A");
      }

      worksheet.addRow(row);
    });

    worksheet.columns.forEach((column) => {
      column.width = 20;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, "products.xlsx");
  };
  
  // Filter and sort the production batches
  const filteredProducts = products
    .filter((product) => {
      switch (selectedSearchField) {
        case "product_code":
          return (
            product.product_code &&
            product.product_code
              .toLowerCase()
              .includes(searchTerm.toLowerCase())
          );
        case "product_name":
          return (
            product.product_name &&
            product.product_name
              .toLowerCase()
              .includes(searchTerm.toLowerCase())
          );
        case "product_description":
          return (
            product.product_description &&
            product.product_description
              .toLowerCase()
              .includes(searchTerm.toLowerCase())
          );
        case "product_category":
          return product.product_category &&
            product.product_category.category_name
            ? product.product_category.category_name
                .toLowerCase()
                .includes(searchTerm.toLowerCase())
            : false;

        case "status":
          return product.status
            ? product.status.toLowerCase().includes(searchTerm.toLowerCase())
            : false;

        default:
          return false;
      }
    })
    .sort((a, b) => {
      if (a[sort.field] < b[sort.field])
        return sort.direction === "asc" ? -1 : 1;
      if (a[sort.field] > b[sort.field])
        return sort.direction === "asc" ? 1 : -1;
      return 0;
    });

  // Base columns
const columns = [
  { field: "id", headerName: "Product ID", flex: 1 },
  { field: "product_name", headerName: "Product Name", flex: 2 },
];

// Add organization column only for Main Admin (after Product Name)
if (userRole === "Main Admin") {
  columns.push({
    field: "organization_name",
    headerName: "Organization",
    width: 170,
    renderCell: (params) => (
      <span>
        {params.row.organization
          ? params.row.organization.organization_name
          : "N/A"}
      </span>
    ),
  });
}

// Add remaining columns
columns.push(
  {
    field: "product_image",
    headerName: "Image",
    flex: 1,
    renderCell: (params) =>
      params.row.product_image ? (
        <img
          src={`${process.env.REACT_APP_IMAGE_BASE_URL}${params.row.product_image}`}
          alt="Product"
          className="h-16 w-16 object-cover rounded-md"
        />
      ) : (
        "No Image"
      ),
  },
  {
    field: "product_description",
    headerName: "Product Description",
    flex: 2,
  },
  {
    field: "product_category",
    headerName: "Category",
    flex: 1,
    renderCell: (params) => (
      <span>
        {params.row.product_category
          ? params.row.product_category.category_name
          : "N/A"}
      </span>
    ),
  },
  {
    field: "unit",
    headerName: "Unit",
    flex: 1,
    renderCell: (params) => (
      <span>{params.row.unit ? params.row.unit.unit_name : "N/A"}</span>
    ),
  },
  {
    field: "status",
    headerName: "Status",
    flex: 1,
    renderCell: (params) => {
      const status = params.row.status;
      const statusClass =
        status === "Active"
          ? "bg-[#388E3C] text-white"
          : "bg-[#E57373] text-white";

      return (
        <span className={`px-2 py-1 rounded-sm ${statusClass}`}>
          {status}
        </span>
      );
    },
  }
);
// Add the Action column for users who are NOT Farm User
if (userRole !== "Farm User") {
  columns.push({
    field: "action",
    headerName: "Action",
    flex: 1,
    renderCell: (params) => (
      <div className="flex justify-left items-left w-10 h-10">
        <button
          onClick={() => handleProductClick(params.row)}
          className="bg-[#28ad4b] text-white px-2 py-1 flex items-center rounded-lg"
        >
          <FaEdit className="text-white mr-2" />
          Edit
        </button>
      </div>
    ),
  });
}

  const handleUpdateProduct = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Authentication token is missing. Please log in again.");
        return;
      }
  
      const formData = new FormData();
      formData.append("product_name", selectedProduct.product_name);
      formData.append("product_category", selectedCategory);
      formData.append("product_description", selectedProduct.product_description);
      formData.append("unit", selectedUnit);
      formData.append("status", selectedProduct.status);
      formData.append("partial_qty_allocation", selectedPartialQty);
      formData.append("qty_modifiable", selectedAllowQuantityModification);
      formData.append("so_inclusive", selectedAllowSalesOrderInclusive);
  
      // Append the new image if a new file is selected
      if (selectedImage) {
        formData.append("product_image", selectedImage); // Attach file
      }
  
      console.log("FormData being sent:", formData); // Debugging log
  
      const response = await axios.put(
        `${API_BASE_URL}/products/update/${selectedProduct.product_code}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data", // Important for file uploads
          },
        }
      );
  
      toast.success("Product Updated Successfully");
      setSelectedProduct(null);
  
      // Refresh the products list
      const updatedProducts = await axios.get(`${API_BASE_URL}/products`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const productsWithId = updatedProducts.data.products.map((product) => ({
        ...product,
        id: product.product_code, // Assign product_code to id
      }));

      console.log("updatedProducts:", productsWithId);
  
      setProducts(productsWithId);
    } catch (error) {
      console.error("Error updating product:", error.response?.data || error.message);
      toast.error(error.response?.data?.message || "Error Updating Product");
    }
  };
  
  return (
    <div className="flex h-screen">
      {/* Main Content */}
      <div className="flex-1 p-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-green-700">Products</h1>

          {(userRole === "Outlet User" || userRole === "Company Admin" || userRole === "Farm User") && (
             <button
             onClick={onAddProductClick}
             className="bg-green-700 text-white px-4 py-2 rounded-lg"
           >
             Add Product
           </button>
          )}
         
        </div>

        {/* Search and Filter */}
        <div className="bg-white p-4 rounded-lg shadow mb-8">
          <div className="flex justify-between mb-4 items-center flex-wrap gap-4">
            {/* Left Section: Search filters */}
            <div className="flex items-center flex-wrap gap-4">
              <select
                className="px-4 py-2 border border-gray-300 rounded-lg"
                value={selectedSearchField}
                onChange={(e) => setSelectedSearchField(e.target.value)}
              >
                <option value="product_name">Product Name</option>
                <option value="product_code">Code</option>
                <option value="product_category">Category</option>
                <option value="product_description">Description</option>
                <option value="status">Status</option>
              </select>

              <div className="relative">
                <input
                  type="text"
                  placeholder="Search Products here"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="px-4 py-2 pl-10 border border-gray-300 rounded-lg w-full max-w-xs"
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <FontAwesomeIcon icon={faSearch} className="text-gray-500" />
                </div>
              </div>
            </div>

            {/* Right Section: Download Excel */}
            <div>
              <button
                onClick={handleDownloadExcel}
                className="flex items-center bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
              >
                <FaDownload className="mr-2" />
                Download Excel
              </button>
            </div>
          </div>          

          {/* Product Grid */}
          <Grid rows={filteredProducts} columns={columns} getRowId={(row) => row.product_code}/>
        </div>

        {/* Popup for Product Details */}
        {/* Popup for Product Details */}
        {selectedProduct && (
          <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50">
            <div className="bg-white p-8 rounded-lg shadow-lg w-full md:w-2/3 lg:w-1/2 relative">
              <button
                onClick={handleClosePopup}
                className="absolute top-2 right-2 bg-red-500 text-white w-8 h-8 flex justify-center items-center rounded-sm hover:bg-red-700"
              >
                &times;
              </button>
              <h1 className="text-2xl font-semibold text-gray-700 mb-4">Update Product</h1>
              
              <div className="grid grid-cols-2 gap-8">
                <div>
                  {/* Product Name */}
                  <div className="mb-4">
                    <label className="block text-gray-700">Product Name</label>
                    <input
                      type="text"
                      value={selectedProduct.product_name}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      onChange={(e) =>
                        setSelectedProduct({
                          ...selectedProduct,
                          product_name: e.target.value,
                        })
                      }
                    />
                  </div>

                  {/* Category Dropdown */}
                  <div className="mb-4">
                    <label className="block text-gray-700">Category</label>
                    <select
                      className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                    >
                      <option value="">Select Product Category</option>
                      {categories.map((category) => (
                        <option key={category._id} value={category.category_name}>
                          {category.category_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Description */}
                  <div className="mb-4">
                    <label className="block text-gray-700">Description</label>
                    <textarea
                      className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      value={selectedProduct.product_description}
                      onChange={(e) =>
                        setSelectedProduct({
                          ...selectedProduct,
                          product_description: e.target.value,
                        })
                      }
                    />
                  </div>

                  {/* Unit Dropdown */}
                  <div className="mb-4">
                    <label className="block text-gray-700">Unit</label>
                    <select
                      className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      value={selectedUnit}
                      onChange={(e) => setSelectedUnit(e.target.value)}
                    >
                      <option value="">Select Product Unit</option>
                      {units.map((unit) => (
                        <option key={unit._id} value={unit.unit_name}>
                          {unit.unit_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Status Dropdown */}
                  <div className="mb-4">
                    <label className="block text-gray-700">Status</label>
                    <select
                      value={selectedProduct.status}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      onChange={(e) =>
                        setSelectedProduct({
                          ...selectedProduct,
                          status: e.target.value,
                        })
                      }
                    >
                      <option value="">Select Status</option>
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>
                  <div className="mb-1">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={allowPartialQuantity}
                          onChange={handlePartialQuantityChange}
                          color="primary"
                        />
                      }
                      label={
                        <>
                          Allow Partial Quantities?{"  "}
                          <span style={{ fontStyle: "italic", color: "gray", fontSize: "medium" }}> (eg: 50.5kg) </span>
                        </>
                      }
                      />
                  </div> 
                  <div className="mb-1">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={allowQuantityModification}
                          onChange={handleQuantityModificationChange}
                          color="primary"
                        />
                      }
                      label={
                        <Tooltip
                          title={
                            <span style={{fontSize: '14px'}}>
                              Allows user to approve more than the requested quantity by the customer.
                            </span>
                          }>
                          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                            Allow Quantity Modification?&nbsp;<FaInfoCircle style={{color: "gray", marginLeft: '4px' }} />
                          </span>
                        </Tooltip>
                      }
                      />
                  </div>    
                  <div className="mb-4">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={allowSalesOrderInclusive}
                          onChange={handleSalesOrderInclusiveChange}
                          color="primary"
                        />
                      }
                      label={
                        <Tooltip 
                          title={
                            <span style={{ fontSize: '14px' }}>
                              These are optional products that can be directly added when creating a Sales Order.
                            </span>
                          }>
                            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                              Additional Product&nbsp;<FaInfoCircle style={{color: "gray", marginLeft: '4px' }} />
                            </span>
                        </Tooltip>
                                      }
                      />
                  </div>     
                </div>
                {/* Image Preview & Upload */}
                <div className="flex flex-col items-center border rounded-lg p-4">
                  {/* Show Existing Image */}
                  {!selectedImage && selectedProduct.product_image && (
                    <img
                      src={imagePreview}
                      alt="Current Product"
                      className="w-64 h-64 object-cover border border-gray-300 rounded-lg mb-4"
                    />
                  )}

                  {/* Show New Image Preview if Selected */}
                  {selectedImage && (
                    <img
                      src={imagePreview}
                      alt="New Product Preview"
                      className="w-100 h-100 object-cover border border-gray-300 rounded-lg mb-4"
                    />
                  )}

                  {/* Product Image Upload Input */}
                  <div className="w-full">
                    <label className="block text-gray-700 text-center mb-20">Product Image</label>
                    <input
                      type="file"
                      accept="image/*"
                      className="w-full p-3 border rounded-lg focus:outline-none"
                      onChange={handleImageChange} // Handle image selection
                    />
                  </div>
                </div>
              </div>

              {/* Update Button - Positioned Below */}
              <div className="mt-6 text-right">
                <button
                  onClick={handleUpdateProduct}
                  className="bg-blue-500 text-white py-3 px-6 rounded-lg hover:bg-blue-600 transition duration-300"
                >
                  Update Product
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Products;
